/**
 * This device's last answer to each query a view ran (docs/views.md#14-queries-and-actions), so a view opens showing
 * what it showed last while it reads again. Answers stay in this device's storage, by view, person, query, and
 * parameters: at most 200 KB an answer and 2 MB in all, the oldest going first. A query whose manifest entry says
 * `"cache": false` is not kept.
 */
import { forget, readJSON, writeJSON } from "./storage";

const PREFIX = "coast:answer:";
const INDEX = "coast:answers";
const MOST = 200_000;
const ALL = 2_000_000;

type Entry = [key: string, size: number, at: number];
const read = (): Entry[] => {
  const list = readJSON<unknown>(INDEX);
  return Array.isArray(list) ? (list as Entry[]) : [];
};
const write = (list: Entry[]) => writeJSON(INDEX, list);
/** Parameters in one order, so the same question finds the same answer. */
const stable = (params: Record<string, unknown>) =>
  JSON.stringify(
    Object.keys(params)
      .sort()
      .map((key) => [key, params[key]]),
  );
export const answerKey = (view: string, person: string, name: string, params: Record<string, unknown>) =>
  `${PREFIX}${view}:${person}:${name}:${stable(params)}`;

/** Every answer kept for a view and person, by `name:params`, the key the frame's copy reads them by (frame/protocol.ts). */
export function keptAnswers(view: string, person: string): Record<string, { value: unknown; at: string }> {
  const prefix = `${PREFIX}${view}:${person}:`,
    out: Record<string, { value: unknown; at: string }> = {};
  for (const [key] of read())
    if (key.startsWith(prefix)) {
      const kept = keptAnswer(key);
      if (kept) out[key.slice(prefix.length)] = kept;
    }
  return out;
}

export function keptAnswer(key: string): { value: unknown; at: string } | null {
  try {
    const kept = JSON.parse(localStorage.getItem(key) || "null");
    return kept && typeof kept.at === "string" ? kept : null;
  } catch {
    return null;
  }
}

export function keepAnswer(key: string, value: unknown) {
  let text: string;
  try {
    text = JSON.stringify({ value, at: new Date().toISOString() });
  } catch {
    return;
  }
  const list = read().filter((entry) => entry[0] !== key);
  if (text.length > MOST) {
    forget(key);
    write(list);
    return;
  }
  list.push([key, text.length, Date.now()]);
  let total = list.reduce((sum, entry) => sum + entry[1], 0);
  while (total > ALL && list.length > 1) {
    const [old] = list.splice(0, 1);
    forget(old![0]);
    total -= old![1];
  }
  try {
    localStorage.setItem(key, text);
  } catch {
    forget(key);
    list.pop();
  }
  write(list);
}
